import localesData from '@/config/locales.json'

export const LANGUAGE_CACHE_KEY = 'LAN'

// Locale codes are data-driven now (src/config/locales.json), so the type is a
// plain string rather than a closed union.
export type SupportedLanguage = string

export interface LocaleMeta {
    code: string
    nativeName: string
    englishName: string
    dir: 'ltr' | 'rtl'
    htmlLang: string
    ogLocale: string
    hreflang: string
    tier: number
}

// ---------------------------------------------------------------------------
// Registry (single source of truth). The edge worker (functions/[[path]].js)
// imports this same locales.json directly. The framework-free static home
// switcher (public/_home/js/home-lang-switcher.js) can't import anything, so
// gen-home-i18n.mjs stamps the list into public/_home/js/locales.gen.js
// (window.__SOVI_LOCALES) at build time. Update locales.json only.
// ---------------------------------------------------------------------------
const ACTIVE_TIER: number = (localesData as { activeTier?: number }).activeTier ?? 0

export const LOCALES: LocaleMeta[] = (localesData.locales as LocaleMeta[])

// A locale ships once its tier is activated. Activate a tier only after its
// translation files exist (enforced by the verify scripts).
export const ACTIVE_LOCALES: LocaleMeta[] = LOCALES.filter(l => l.tier <= ACTIVE_TIER)

export const SUPPORTED_LANGUAGES: SupportedLanguage[] = ACTIVE_LOCALES.map(l => l.code)

const SUPPORTED_SET = new Set(SUPPORTED_LANGUAGES)

export const localeMeta = (code?: string | null): LocaleMeta | undefined =>
    code ? LOCALES.find(l => l.code === code) : undefined

// 将任意 locale 字符串（?lang= 查询参数 / navigator.language）归一化为受支持的语言码，
// 不支持时返回 ''。处理地区/书写变体：de-DE → de，zh-Hant / zh-TW → zh-tw，zh-* → zh-cn，
// pt-PT → pt-pt（精确命中），裸 pt / 其它 pt-XX（如旧缓存的 'pt'、pt-AO）→ pt-br。
export const normalizeLanguage = (raw?: string | null): SupportedLanguage | '' => {
    if (!raw) return ''
    const lower = raw.trim().toLowerCase()
    if (SUPPORTED_SET.has(lower)) return lower
    if (lower.startsWith('zh')) {
        const zh = /hant|tw|hk|mo/.test(lower) ? 'zh-tw' : 'zh-cn'
        return SUPPORTED_SET.has(zh) ? zh : ''
    }
    // 葡语只区分巴西/葡萄牙两种：pt-br / pt-pt 由上面的精确匹配处理；
    // 裸 pt 及未识别的 pt-XX（含老用户缓存里的 'pt'）默认归到巴西葡语。
    if (lower === 'pt' || lower.startsWith('pt-')) {
        return SUPPORTED_SET.has('pt-br') ? 'pt-br' : ''
    }
    const base = lower.split('-')[0]
    return SUPPORTED_SET.has(base) ? base : ''
}

// 读取当前 URL 上的 ?lang= 查询参数（仅 Web 环境）并归一化
export const getQueryLanguage = (): SupportedLanguage | '' => {
    if (typeof window === 'undefined' || !window.location) return ''
    try {
        return normalizeLanguage(new URLSearchParams(window.location.search).get('lang'))
    } catch {
        return ''
    }
}

// 获取浏览器语言
export const getBrowserLanguage = (): string => {
    if (typeof navigator === 'undefined') return ''
    return normalizeLanguage(navigator.language)
}

// 解析当前生效语言：?lang= 查询参数（命中即写回缓存）> 本地缓存 > 'en'。
// 让落地页 https://mysovi.ai/?lang=de 的语言选择能延续到 SPA 内部导航。
export const getStorageLanguage = (): SupportedLanguage => {
    if (typeof localStorage === 'undefined') return 'en'

    const fromQuery = getQueryLanguage()
    if (fromQuery) {
        try {
            localStorage.setItem(LANGUAGE_CACHE_KEY, fromQuery)
        } catch {
            // ignore storage write failures (private mode etc.)
        }
        return fromQuery
    }

    // Re-normalize the cached value so a now-inactive/legacy code falls back to 'en'.
    return normalizeLanguage(localStorage.getItem(LANGUAGE_CACHE_KEY)) || 'en'
}

// Apply a locale's writing direction + lang to <html>. Call on i18n init and on
// every switch so RTL locales (ar) render correctly and `lang` stays accurate.
export const applyLocaleDir = (code: string): void => {
    if (typeof document === 'undefined') return
    const meta = localeMeta(code)
    const el = document.documentElement
    el.setAttribute('dir', meta?.dir || 'ltr')
    el.setAttribute('lang', meta?.htmlLang || code || 'en')
}
